import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
  setDoc,
  getDoc,
  query,
  orderBy
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Registration, EventSettings } from './types';
import { DEFAULT_SETTINGS } from './eventData';

const firebaseConfig = {
  apiKey: "AIzaSyB9ln_KUzFv6YiIqmSxeccNkWXCO8H6cgc",
  authDomain: "eventos-ipcc.firebaseapp.com",
  projectId: "eventos-ipcc",
  storageBucket: "eventos-ipcc.firebasestorage.app",
  messagingSenderId: "486600949849",
  appId: "1:486600949849:web:623dcffbd7e9955c5f3148"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const storage = getStorage(app);

/**
 * Identificador do evento. Mantém os dados deste congresso separados
 * do Seminário de Mulheres (que usava `registrations` e `settings/event`).
 * Precisa ser o mesmo valor usado em functions/index.js.
 */
export const EVENT_ID = "homens2026";
const REGISTRATIONS_COLLECTION = `registrations_${EVENT_ID}`;
const SETTINGS_DOC = doc(db, "settings", EVENT_ID);

const CLOUD_FUNCTIONS_BASE_URL = "https://us-central1-eventos-ipcc.cloudfunctions.net";

export const dbService = {
  getRegistrations: async (): Promise<Registration[]> => {
    const q = query(collection(db, REGISTRATIONS_COLLECTION), orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Registration[];
  },
  addRegistration: async (reg: Omit<Registration, 'id' | 'createdAt' | 'status'>): Promise<Registration> => {
    const newReg = { ...reg, createdAt: Date.now(), status: 'pending' as const };
    const docRef = await addDoc(collection(db, REGISTRATIONS_COLLECTION), newReg);
    return { id: docRef.id, ...newReg };
  },
  updatePaymentStatus: async (id: string, status: 'paid'): Promise<void> => {
    await updateDoc(doc(db, REGISTRATIONS_COLLECTION, id), { status });
  },
  /**
   * Envia o comprovante de pagamento (imagem ou PDF) para o Storage e
   * grava o link na inscrição. O status continua "pending" até o admin dar baixa.
   */
  uploadReceipt: async (registrationId: string, file: File): Promise<Partial<Registration>> => {
    const safeName = file.name.replace(/[^\w.\-]+/g, '_').slice(-80);
    const storageRef = ref(storage, `receipts/${EVENT_ID}/${registrationId}/${Date.now()}_${safeName}`);
    await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
    const receiptUrl = await getDownloadURL(storageRef);
    const patch = {
      receiptUrl,
      receiptName: file.name,
      receiptType: file.type || '',
      receiptUploadedAt: Date.now()
    };
    await updateDoc(doc(db, REGISTRATIONS_COLLECTION, registrationId), patch);
    return patch;
  },
  /** Marca que o inscrito vai enviar o comprovante pelo WhatsApp. */
  markReceiptViaWhatsApp: async (registrationId: string): Promise<void> => {
    await updateDoc(doc(db, REGISTRATIONS_COLLECTION, registrationId), { receiptViaWhatsApp: true });
  },
  deleteRegistration: async (id: string): Promise<void> => {
    await deleteDoc(doc(db, REGISTRATIONS_COLLECTION, id));
  },
  /**
   * Retorna as configurações do evento. Se ainda não houver nada salvo no
   * Firestore, usa os padrões de eventData.ts (o site funciona sem configurar nada).
   */
  getSettings: async (): Promise<EventSettings> => {
    const docSnap = await getDoc(SETTINGS_DOC);
    if (!docSnap.exists()) return { ...DEFAULT_SETTINGS };
    const stored = docSnap.data() as Partial<EventSettings>;
    const merged: EventSettings = { ...DEFAULT_SETTINGS, ...stored };
    // Listas vazias no banco não devem apagar os padrões
    if (!stored.speakers || stored.speakers.length === 0) merged.speakers = DEFAULT_SETTINGS.speakers;
    if (!stored.schedule || stored.schedule.length === 0) merged.schedule = DEFAULT_SETTINGS.schedule;
    if (!stored.checklist || stored.checklist.length === 0) merged.checklist = DEFAULT_SETTINGS.checklist;
    return merged;
  },
  saveSettings: async (settings: EventSettings): Promise<void> => {
    await setDoc(SETTINGS_DOC, settings);
  },
  uploadImage: async (file: File): Promise<string> => {
    const storageRef = ref(storage, `event/${EVENT_ID}/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  },
  getPagSeguroSession: async () => {
    try {
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/getPagSeguroSession`);
      if (!res.ok) throw new Error("Erro na rede ao obter sessão");
      const data = await res.json();
      return data.sessionId;
    } catch (e) {
      console.error("Erro getPagSeguroSession:", e);
      return null;
    }
  },
  processPayment: async (paymentData: any) => {
    try {
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/processPayment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: paymentData })
      });
      if (!res.ok) throw new Error("Erro de comunicação com o servidor de pagamento");
      return await res.json();
    } catch (e) {
      console.error("Erro processPayment:", e);
      return { data: { success: false, message: "Erro de conexão com o servidor de pagamento." } };
    }
  },
  createCheckoutRedirect: async (paymentData: any) => {
    try {
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/createCheckoutRedirect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: paymentData })
      });
      if (!res.ok) throw new Error("Erro de comunicação com o servidor de redirecionamento");
      return await res.json();
    } catch (e) {
      console.error("Erro createCheckoutRedirect:", e);
      return { data: { error: "Erro de conexão com o servidor." } };
    }
  }
};
